import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { openModal, resetFilters, runComparaison, setOptionQuery, TComparaisonFilters, updateCheckExam, updateFilters } from '../../store/reducers/comparaison.reducer';
import { useHorses } from '../../hooks/useHorses';
import { TUser } from '../../shared/types/user.type';
import { Badge, Button, CircularProgress, OutlinedInput, Popover, TextField, Typography, useTheme } from '@mui/material';
import { t } from 'i18next';
import { Search } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { TTrainerHorse } from '../../shared/types/trainerHorse.type';
import ComparaisonList from '../comparaison/ComparaisonList';
import ListSkeleton from '../exams/loader/ListSkeleton';
import { Ttraining, TTrainingType } from '../../shared/types/training.type';
import { debounce, isEmpty, lowerCase, set, uniqBy } from 'lodash';
import { Icon } from '@iconify/react';
import ComparaisonHorseFilters from '../comparaison/ComparaisonHorseFilters';
import { useTracks } from '../../hooks/useTracks';
import EmptyAnimation from '../../lottie/animationEmpty.json'
import dayjs from 'dayjs';
import Lottie from 'lottie-react';

export default function ComparaisonModal() {
    const open = useSelector<RootState>(state => state.comparaison.open) as boolean
    const filters = useSelector<RootState>(state => state.comparaison.filters) as TComparaisonFilters
    const isUserLoading = useSelector<RootState>(state => state.user.isUserLoading) as boolean
    const auth = useSelector<RootState>(state => state.user.user) as TUser | null
    const [Horses, setHorses] = React.useState<TTrainerHorse[]>([])
    const [options, setOptions] = React.useState<string>('')
    const dispatch = useDispatch()
    const theme = useTheme();

    const { horses, isLoadingHorses } = useHorses(open && !isUserLoading, auth?.['@id'], {
        rawHorseOptions: options
    })
    const [search, setSearch] = React.useState('')

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const opened = Boolean(anchorEl);
    const id = opened ? 'simple-popover' : undefined;

    const { tracks, trainingTypes, isTrackLoading, isLoadingTrainingTypes } = useTracks(open && !isUserLoading, auth?.id, true, true)


    React.useEffect(() => {
        if (isEmpty(horses)) return
        setHorses(horses)
    }, [horses])

    React.useEffect(() => {
        if (!horses || !open) return

        let filtered = horses.filter(e => lowerCase(e.horse?.name).indexOf(lowerCase(search)) !== -1)

        setHorses(filtered);

    }, [open, search, horses.length])

    const isFiltersEmpty = React.useMemo(() => {
        if (!filters) return true
        return isEmpty(filters.selectedTracks) && isEmpty(filters.selectedTrainingTypes) && !filters.startDate && !filters.endDate
    }, [filters])

    const closeDrawer = () => {
        dispatch(openModal(false))
        dispatch(runComparaison(true))
    }


    const handleExamClick = (checked: boolean, exam: Ttraining) => {
        dispatch(updateCheckExam({ checked, exam }))
    }


    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onFilterReset = React.useCallback(() => {
        setOptions('')
        dispatch(setOptionQuery(''))
        dispatch(resetFilters())
    }, [])

    const debouncedSearch = debounce((value: string) => {
        setSearch(value)
    }, 300)

    function getQueryObject<T>(data: T[], key: string, valuePath: keyof T) {
        let str: string[] = []

        data.forEach(item => {
            let value = encodeURIComponent(String(item[valuePath]))
            str.push(`&${key}=${value}`)
        })
        return str.join('')
    }

    const onFilterSave = () => {

        let mergedTracks = !isEmpty(filters?.selectedTracks) ? getQueryObject(filters?.selectedTracks, encodeURIComponent('with-training-track-name[]'), '@id') : ''
        let mergedtypes = !isEmpty(filters?.selectedTrainingTypes) ? getQueryObject(filters?.selectedTrainingTypes, encodeURIComponent('with-training-training-type[]'), '@id') : ''

        let optionStr = mergedTracks
            .concat(mergedtypes)
            .concat(filters?.startDate ? `&with-trainings-date-start=${dayjs(filters?.startDate).utc(true).toISOString()}` : '')
            .concat(filters?.endDate ? `&with-trainings-date-end=${dayjs(filters?.endDate).utc(true).toISOString()}` : '')


        setOptions(optionStr)

        dispatch(setOptionQuery(optionStr))
        handleClose()
    }

    const DrawerList = (
        <Box sx={{ flex: 2, overflowY: 'auto', paddingBottom: '60px' }} role="presentation" onClick={closeDrawer}>
            {
                isLoadingHorses ?
                    <ListSkeleton></ListSkeleton> :
                    isEmpty(horses) ?
                        <Box display={'flex'} flexDirection={'column'} alignItems={'center'} height={'100%'}>
                            <Lottie loop={false} style={{ width: '120px' }} animationData={EmptyAnimation}></Lottie>
                            <Typography variant='subtitle2'>{t('filterTrainings:noResults')}</Typography>
                        </Box>
                        :
                        <ComparaisonList handleExamClick={handleExamClick} horses={uniqBy(Horses, trainer => trainer?.horse?.id)}></ComparaisonList>
            }
        </Box>
    );

    if (!open) return null


    return (
        <div>
            <Drawer open={open} anchor='right' onClose={(e, r) => {
                closeDrawer()
            }} >
                <Box display={'flex'} flexDirection={'column'} position={'relative'} sx={{ height: '100%' }}>
                    <Box p={2} display={'flex'} flexDirection={'column'}>
                        <Typography variant='subtitle1'>{t('compareHorseSelection:title')}</Typography>
                        <Box display={'flex'} gap={1}>
                            <OutlinedInput onChange={e => debouncedSearch(e.target.value)} size='small' fullWidth placeholder={t('eap:table:filters:search')} startAdornment={<Search color='disabled'></Search>} ></OutlinedInput>
                            <Badge color='info' invisible={isFiltersEmpty} variant='dot'>
                                <LoadingButton loading={isTrackLoading || isLoadingTrainingTypes} aria-describedby={id} variant="contained" onClick={handleClick} startIcon={<Icon icon={'fluent:filter-28-filled'}></Icon>}>{t('eap:table:filters:filters')}</LoadingButton>
                            </Badge>
                        </Box>

                    </Box>
                    <Popover
                        id={id}
                        open={opened}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        PaperProps={{
                            style: {
                                overflow: 'visible',
                                backgroundColor: "#181818ff", // your custom bg
                            },
                        }}
                    >
                        <ComparaisonHorseFilters
                            tracks={tracks}
                            trainingTypes={trainingTypes}
                            selectedTracks={filters?.selectedTracks}
                            selectedTrainingTypes={filters?.selectedTrainingTypes}
                            onTracksChange={(tracks) => dispatch(updateFilters({ ...filters, selectedTracks: tracks }))}
                            onTrainingTypesChange={(trainingTypes) => dispatch(updateFilters({ ...filters, selectedTrainingTypes: trainingTypes }))}
                            onReset={onFilterReset}
                            onSave={onFilterSave}
                            startDate={filters?.startDate ?? null}
                            endDate={filters?.endDate ?? null}
                            onEndDateChange={(value) => dispatch(updateFilters({ ...filters, endDate: value }))}
                            onStartDateChange={(value) => dispatch(updateFilters({ ...filters, startDate: value }))}
                            isFiltersEmpty={isFiltersEmpty}
                        ></ComparaisonHorseFilters>
                    </Popover>

                    {DrawerList}
                    <Box sx={{
                        // backgroundColor: '#1c1e26',
                        borderTop: '1px solid whitesmoke'
                    }} display={'flex'} position={'sticky'} bottom={0} justifyContent={'flex-end'} gap={1} p={2}>
                        <LoadingButton onClick={closeDrawer} variant='contained'> {t('button:close')}</LoadingButton>
                    </Box>
                </Box>

            </Drawer>
        </div>
    );
}